uniform vec3 uColorA;
uniform vec3 uColorB;
uniform float uTime;
uniform float uAspect;

varying vec2 vUv;

// Simplex 3D Noise 
vec4 permute(vec4 x){return mod(((x*34.0)+1.0)*x, 289.0);}
vec4 taylorInvSqrt(vec4 r){return 1.79284291400159 - 0.85373472095314 * r;}

float snoise(vec3 v){ 
  const vec2  C = vec2(1.0/6.0, 1.0/3.0) ;
  const vec4  D = vec4(0.0, 0.5, 1.0, 2.0);

  vec3 i  = floor(v + dot(v, C.yyy) );
  vec3 x0 = v - i + dot(i, C.xxx) ;

  vec3 g = step(x0.yzx, x0.xyz);
  vec3 l = 1.0 - g;
  vec3 i1 = min( g.xyz, l.zxy );
  vec3 i2 = max( g.xyz, l.zxy );

  vec3 x1 = x0 - i1 + 1.0 * C.xxx;
  vec3 x2 = x0 - i2 + 2.0 * C.xxx;
  vec3 x3 = x0 - 1.0 + 3.0 * C.xxx;

  i = mod(i, 289.0 ); 
  vec4 p = permute( permute( permute( 
             i.z + vec4(0.0, i1.z, i2.z, 1.0 ))
           + i.y + vec4(0.0, i1.y, i2.y, 1.0 )) 
           + i.x + vec4(0.0, i1.x, i2.x, 1.0 ));

  float n_ = 1.0/7.0; // N=7
  vec3  ns = n_ * D.wyz - D.xzx;

  vec4 j = p - 49.0 * floor(p * ns.z *ns.z);  

  vec4 x_ = floor(j * ns.z);
  vec4 y_ = floor(j - 7.0 * x_ );    

  vec4 x = x_ *ns.x + ns.yyyy;
  vec4 y = y_ *ns.x + ns.yyyy;
  vec4 h = 1.0 - abs(x) - abs(y);

  vec4 b0 = vec4( x.xy, y.xy );
  vec4 b1 = vec4( x.zw, y.zw );

  vec4 s0 = floor(b0)*2.0 + 1.0;
  vec4 s1 = floor(b1)*2.0 + 1.0;
  vec4 sh = -step(h, vec4(0.0));

  vec4 a0 = b0.xzyw + s0.xzyw*sh.xxyy ;
  vec4 a1 = b1.xzyw + s1.xzyw*sh.zzww ;

  vec3 p0 = vec3(a0.xy,h.x);
  vec3 p1 = vec3(a0.zw,h.y);
  vec3 p2 = vec3(a1.xy,h.z);
  vec3 p3 = vec3(a1.zw,h.w);

  vec4 norm = taylorInvSqrt(vec4(dot(p0,p0), dot(p1,p1), dot(p2, p2), dot(p3,p3)));
  p0 *= norm.x;
  p1 *= norm.y;
  p2 *= norm.z;
  p3 *= norm.w;

  vec4 m = max(0.6 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0);
  m = m * m;
  return 42.0 * dot( m*m, vec4( dot(p0,x0), dot(p1,x1), 
                                dot(p2,x2), dot(p3,x3) ) );
}

void main(){

    // Base radial gradient
    float mixStrength = (length(vUv - 0.5));
    mixStrength = pow(mixStrength, 0.5); // softened the gradient a bit
    
    // Add soft perlin noise for a blurry/cloudy background variance
    // Passing uTime into the Z axis of the 3D noise ensures it morphs IN PLACE instead of sliding on the X/Y axes!
    float noiseValue = snoise(vec3(vUv * 1.2, uTime * 0.0003)) * 0.25;
    mixStrength += noiseValue;

    // Add fine cinematic grain (high frequency noise)
    float baseGrain = fract(sin(dot(vUv.xy, vec2(12.9898,78.233))) * 43758.5453) * 0.04;
    
    // Give the grain a radial intensity (stronger at edges, weaker at center)
    float radialStrength = length(vUv - 0.5) * 1.5; 
    float grain = baseGrain * radialStrength;

    vec3 color = mix(uColorA, uColorB, mixStrength) + grain;

    // Create a smooth grid pattern with fixed aspect ratio (perfect squares)
    vec2 gridUv = vUv - 0.5; // center the coordinates
    gridUv.x *= uAspect; // stretch the X axis perfectly against the screen aspect ratio
    gridUv = gridUv * 40.0;
    
    vec2 gridDist = abs(fract(gridUv) - 0.5);
    float gridLine = smoothstep(0.47, 0.5, max(gridDist.x, gridDist.y));

    // Show the grid where the background gets darker (high mixStrength threshold)
    float gridVisibility = smoothstep(0.3, 0.9, mixStrength);
    
    // Add the white grid on top using the line value and visibility mask
    // color = mix(color, vec3(1.0), gridLine * gridVisibility * 0.01);

    gl_FragColor = vec4(color, 1.0);
}